import React, { useCallback, useContext, useMemo, useState } from 'react';
import {
  Typography,
  Tabs,
  Tab,
  Box,
  Button, Stack,
} from '@mui/material';
import { OverviewTab } from '@modules/Analysis/partials/OvierviewTab';
import { StoryTab } from '@modules/Analysis/partials/StoryTab';
import { PerformanceTab } from '@modules/Analysis/partials/PerformanceTab';
import { TenseTab } from '@modules/Analysis/partials/TenseTab';
import { useStoryQuery } from '@modules/Stories/hooks/useStoryQuery';
import { useAnalysisCrud } from '@modules/Analysis/hooks/useAnalysisCrud';
import { Autorenew } from '@mui/icons-material';
import { DialogContext, FeedbackContext } from '@cvt/contexts';
import { format } from 'date-fns';



type StructuralAnalysisProps = {
  storyId: string | number;
  initialTab?: Analysis.AnalysisTab;
  title?: string;
  loadingText: string;
  isMobile?: boolean;
};

export const StructuralAnalysis: React.FC<StructuralAnalysisProps> = ({
  storyId,
  title = 'AI Structural Story Analysis',
  initialTab = 'overview',
  loadingText,
  isMobile,
}) => {

  const [selectedTab, setSelectedTab] = useState<string>(initialTab);
  const { createStructuralAnalysis } = useAnalysisCrud();
  const { openDialog } = useContext(DialogContext);
  const { triggerFeedback } = React.useContext(FeedbackContext);

  const handleRunAnalysis = useCallback(
    async (id: number) => {
      try {
        await createStructuralAnalysis.mutateAsync({ id });
        openDialog('structuralAnalysis', { storyId: id });
      } catch {
        triggerFeedback({
          severity: 'error',
          message: 'Smth. went wrong' ,
        });
      }
    },
    [createStructuralAnalysis, openDialog, triggerFeedback],
  );

  const { story } = useStoryQuery({ id: Number(storyId) }, { enabled: !!storyId });
  const isStoryRecentlyModified = !!story?.modified;
  const isStoryFeedbackRecentlyModified = !!story?.structuralFeedback?.modified;

  const showWarning = useMemo(() => {
    if (!isStoryRecentlyModified || !isStoryFeedbackRecentlyModified) return false;

    const storyDate = new Date(story!.modified!);
    const feedbackDate = new Date(story!.structuralFeedback!.modified!);

    if (isNaN(storyDate.getTime()) || isNaN(feedbackDate.getTime())) return false;

    const feedbackPlusTolerance = new Date(feedbackDate);
    feedbackPlusTolerance.setSeconds(feedbackPlusTolerance.getSeconds() + 5);

    return storyDate > feedbackPlusTolerance;
  }, [
    isStoryRecentlyModified,
    isStoryFeedbackRecentlyModified,
    story,
  ]);

  const { closeDialog } = useContext(DialogContext);

  const handleTabChange = (_: React.SyntheticEvent, value: Analysis.AnalysisTab) => {
    setSelectedTab(value);
  };

  if (!story) {
    return 'Loading...';
  }
  return (
    <>
      {isMobile && (
        <Button variant="outlined" color="primary" sx={{ mb:1 }}  onClick={()=> closeDialog('structuralAnalysis')}>
            Back
        </Button>
      )}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          ...(showWarning && { backgroundColor: 'warning.main', p: 2, borderRadius: 1 }),
        }}
      >
        <Box>
          <Typography variant="h2">
            {title}: {story.title}
          </Typography>
          {story.structuralFeedback && <Typography variant="h5">
            {story?.structuralFeedback && (
              <Typography variant="h5">
                {`last modified ${format(story.structuralFeedback.modified, 'MMM d, yyyy')}`}
              </Typography>
            )}
          </Typography>}
          {showWarning && (
            <Typography variant="body2" sx={{ mt: 1 }}>
                The story was changed after the last analysis, you should consider re-running.
            </Typography>
          )}
        </Box>
        {story && (<Button variant="outlined" onClick={() => handleRunAnalysis(story.id)}
          disabled={createStructuralAnalysis.isPending}>
          <Autorenew sx={{
            animation: createStructuralAnalysis.isPending ? 'spin 1s linear infinite' : 'none',
            '@keyframes spin': {
              from: {
                transform: 'rotate(0deg)',
              },
              to: {
                transform: 'rotate(360deg)',
              },
            },
          }}
          />
          {story.structuralFeedback ? 'Re-run Structural Analysis' : 'Run Structural Analysis'}
        </Button>)}
      </Stack>
      {!story.structuralFeedback ? (
        <Typography variant="body1" sx={{ mt: 2 }}>
          {loadingText}
        </Typography>
      ) : (
        <>
          <Box mt={1} mb={2}>
            <Tabs
              value={selectedTab}
              onChange={handleTabChange}
              variant="fullWidth"
              centered
            >
              <Tab label="Overview" value="overview"/>
              <Tab label="Story" value="story"/>
              <Tab label="Performance" value="performance"/>
              <Tab label="Tense" value="tense"/>
            </Tabs>
          </Box>

          {selectedTab === 'overview' && <OverviewTab story={story}/>}
          {selectedTab === 'story' && <StoryTab story={story}/>}
          {selectedTab === 'performance' && <PerformanceTab story={story}/>}
          {selectedTab === 'tense' && <TenseTab story={story}/>}
        </>
      )}
    </>
  );
};
